import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  createJobQuote,
  getJobFloorsForJob,
  getJobQuotesForJob,
  JobQuote,
  replaceJobFloorsForJob,
} from '../../admin/admin.api';
import { logError } from '../../../utils/logger';
import { ModalWrapper, SectionTitle } from '../../shared/components';
import {
  buildJobFloorRows,
  jobFloorRowsToFormValues,
} from '../domain/mappers/jobFloors.mapper';
import { buildJobQuotePayload, buildMilestoneRows } from '../domain/mappers/jobQuote.mapper';
import JobQuotationList from './JobQuotationList';
import {
  LockedJobTypes,
  workAgeFromProjectType,
  workCategoryFromConstructionWorkType,
} from '../domain/workJobLookups';
import { defaultValues } from '../features/project-form/schema/defaultValues';
import StepAccounting from '../features/project-form/steps/StepAccounting';
import StepTypeOfWork from '../features/project-form/steps/StepTypeOfWork';
import {
  getJobMilestones,
  JobMilestoneRow,
  saveJobMilestones,
  WorkJobReportRow,
} from './workJobReport.api';

export type JobStepKey = 'typeOfWork' | 'quotation';

/**
 * Only the heading: each step renders its own component below, because both now take
 * props this dialog supplies (the job's locked types; the hidden identifier/milestone
 * sections) rather than being interchangeable.
 */
const STEPS = {
  typeOfWork: { title: 'WORK DETAILS' },
  quotation: { title: 'QUOTATION' },
} as const;

interface JobStepDialogProps {
  /** Which step to show; null keeps the dialog closed. */
  step: JobStepKey | null;
  /** The job the panel is bound to — its code and name head the dialog. */
  row: WorkJobReportRow | null;
  onClose: () => void;
  /** Called after floors are saved, so the caller can refresh what it shows. */
  onSaved?: () => void;
}

/**
 * Shows the New Job Creation screen's "Type of Work" and "Accounting" panels over the
 * Edit Job dialog, reusing those exact step components rather than a copy. Opened from a
 * job they are headed "Work Details" and "Quotation", which is what they are at that point.
 *
 * Work Details is live: it loads the job's `job_floors` rows, and Submit writes them back.
 * The project type, construction work type and building type it shows are read-only labels
 * from `work_jobs` — they belong to the Edit Job form behind this dialog, and duplicating
 * them as editable radios here would let the two disagree.
 *
 * Accounting is still preview-only; its quote fields have no save path yet.
 *
 * Remounted per step and per job (`key`) so neither inherits the other's state.
 */
export default function JobStepDialog({ step, row, onClose, onSaved }: JobStepDialogProps) {
  if (!step || !row) return null;
  return (
    <StepForm key={`${step}-${row.id}`} step={step} row={row} onClose={onClose} onSaved={onSaved} />
  );
}

function StepForm({
  step,
  row,
  onClose,
  onSaved,
}: {
  step: JobStepKey;
  row: WorkJobReportRow;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const { title } = STEPS[step];
  const isTypeOfWork = step === 'typeOfWork';

  const lockedTypes: LockedJobTypes = useMemo(
    () => ({
      projectType: row.project_type,
      constructionWorkType: row.construction_work_type,
      buildingType: row.building_type,
    }),
    [row.project_type, row.construction_work_type, row.building_type]
  );

  /**
   * The job's own type selections, as the form's internal enums. They drive which sections
   * the step renders (allied vs building, new vs existing), so they are seeded even though
   * they are no longer editable here.
   */
  const seededTypes = useMemo(
    () => ({
      workAge: workAgeFromProjectType(row.project_type) ?? '',
      workCategory: workCategoryFromConstructionWorkType(row.construction_work_type) ?? '',
      buildingType: row.building_type ?? '',
    }),
    [row.project_type, row.construction_work_type, row.building_type]
  );

  /**
   * The job's total area, on both panels: Work Details collects it, and the QUOTATION panel
   * shows it as the read-only Area — one area per job rather than one per quotation.
   */
  const seededArea = {
    totalAreaSqFt: row.total_area_sqft == null ? '' : String(row.total_area_sqft),
  };

  const methods = useForm({
    defaultValues: isTypeOfWork
      ? { ...defaultValues, ...seededTypes, ...seededArea }
      : { ...defaultValues, ...seededArea },
    mode: 'onTouched',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The job's quotation history, newest first. Quotation step only. */
  const [quotes, setQuotes] = useState<JobQuote[]>([]);

  /**
   * `construction_work_type_id` is nullable on `work_jobs`, and without it the step has no
   * work category to branch on: every section stays hidden and the drawing counts collapse
   * to the empty-form defaults. Saving that would write a floor set describing nothing, so
   * the job has to be classified on the Edit Job screen first.
   */
  const canSubmit = Boolean(seededTypes.workCategory);

  // Load the saved floors so the panel opens showing what is stored. Submit replaces the
  // whole set, so starting from a blank form would quietly discard everything already there.
  useEffect(() => {
    if (!isTypeOfWork) return;
    let cancelled = false;

    (async () => {
      try {
        const floors = await getJobFloorsForJob(row.id);
        if (cancelled) return;
        methods.reset({
          ...defaultValues,
          ...seededTypes,
          ...seededArea,
          ...jobFloorRowsToFormValues(floors as any[]),
        });
      } catch (err) {
        if (cancelled) return;
        setError(
          logError({
            message: 'Failed to load saved floors for this job',
            error: err,
            code: 'JOB_FLOORS_LOAD_FAILED',
          })
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTypeOfWork, row.id, methods, seededTypes, row.total_area_sqft]);

  /**
   * Load the quotation history, and the job's milestones.
   *
   * The quotation fields themselves stay blank: a submit raises a *new* quotation rather
   * than editing one, so seeding them from the current quote would invite an accidental
   * duplicate of a figure nobody meant to change. Milestones are the opposite — they belong
   * to the job, not to one quotation, so the table opens on what is saved and a submit
   * updates it rather than adding a second set.
   */
  useEffect(() => {
    if (isTypeOfWork) return;
    let cancelled = false;

    (async () => {
      try {
        const [loaded, milestones] = await Promise.all([
          getJobQuotesForJob(row.id),
          getJobMilestones(row.id).catch(() => [] as JobMilestoneRow[]),
        ]);
        if (cancelled) return;
        setQuotes(loaded);
        if (milestones.length) methods.reset(withMilestones(methods.getValues(), milestones));
      } catch (err) {
        if (cancelled) return;
        setError(
          logError({
            message: 'Failed to load quotations for this job',
            error: err,
            code: 'JOB_QUOTES_LOAD_FAILED',
          })
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isTypeOfWork, row.id, methods]);

  /** Back to an empty panel, keeping the job's own types — those are not this form's to clear. */
  const handleClear = () => {
    methods.reset(
      isTypeOfWork
        ? { ...defaultValues, ...seededTypes, ...seededArea }
        : { ...defaultValues, ...seededArea }
    );
    setError(null);
  };

  const handleSubmit = methods.handleSubmit(async (values) => {
    setSaving(true);
    setError(null);
    try {
      if (isTypeOfWork) {
        await replaceJobFloorsForJob(row.id, buildJobFloorRows(values), values.totalAreaSqFt ?? null);
        toast.success(`Work details saved for ${row.job_code}`);
        onSaved?.();
        onClose();
        return;
      }

      await createJobQuote(row.id, buildJobQuotePayload(values));

      /**
       * Milestones after the quotation, and reported separately: the quotation is the thing
       * being raised, and it is already saved by this point. Failing quietly here would
       * leave the table on screen disagreeing with the database, so a milestone failure
       * says exactly that rather than reading as "the quotation did not save".
       */
      const milestones = buildMilestoneRows(values);
      try {
        await saveJobMilestones(row.id, milestones);
        toast.success(`Quotation raised for ${row.job_code}`);
      } catch (err) {
        setError(
          logError({
            message: `Quotation raised for ${row.job_code}, but its milestones could not be saved`,
            error: err,
            code: 'JOB_MILESTONES_SAVE_FAILED',
          })
        );
      }

      // Stay open on the refreshed history: the new quotation is now the one in force,
      // and seeing it listed is the confirmation that it saved.
      setQuotes(await getJobQuotesForJob(row.id));
      // The milestones stay on screen — they are the job's, and are now what was just saved.
      methods.reset(withMilestones({ ...defaultValues, ...seededArea }, milestones));
      onSaved?.();
    } catch (err) {
      setError(
        logError({
          message: isTypeOfWork ? 'Failed to save work details' : 'Failed to raise quotation',
          error: err,
          code: isTypeOfWork ? 'JOB_FLOORS_SAVE_FAILED' : 'JOB_QUOTE_SAVE_FAILED',
        })
      );
    } finally {
      setSaving(false);
    }
  });

  return (
    <ModalWrapper open onClose={saving ? () => {} : onClose} maxWidth='lg'>
      <SectionTitle
        title={`${title} · ${row.job_code}`}
        meta={
          <Typography variant='body2' color='text.secondary'>
            {row.job_name}
          </Typography>
        }
      />

      {error && <Alert severity='error'>{error}</Alert>}

      {isTypeOfWork && !canSubmit && (
        <Alert severity='warning' variant='outlined'>
          This job has no construction work type set, so there is nothing to describe yet.
          Set it on the Edit Job screen first.
        </Alert>
      )}

      {loading ? (
        <Stack alignItems='center' py={4}>
          <CircularProgress size={28} />
        </Stack>
      ) : (
        <FormProvider {...methods}>
          {!isTypeOfWork && (
            <Box sx={{ mb: 3 }}>
              <JobQuotationList quotes={quotes} onChanged={async () => setQuotes(await getJobQuotesForJob(row.id))} />
            </Box>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {isTypeOfWork ? (
              <StepTypeOfWork lockedTypes={lockedTypes} title='Work Details' />
            ) : (
              <StepAccounting hideIdentifiers />
            )}

            <Stack direction='row' gap={1.5} justifyContent='flex-end' sx={{ mt: 2.5 }}>
              <Button onClick={onClose} disabled={saving}>
                {isTypeOfWork ? 'Cancel' : 'Close'}
              </Button>
              <Button
                type='button'
                onClick={handleClear}
                disabled={saving || (isTypeOfWork && !canSubmit)}
              >
                Clear
              </Button>
              <Button
                type='submit'
                variant='contained'
                disabled={saving || (isTypeOfWork && !canSubmit)}
              >
                {saving ? 'Saving…' : isTypeOfWork ? 'Submit' : 'Raise Quotation'}
              </Button>
            </Stack>
          </form>
        </FormProvider>
      )}
    </ModalWrapper>
  );
}

/**
 * The saved milestones, as the form's own `milestones` branch.
 *
 * Takes any row carrying the four API keys — what the job detail returns and what the save
 * sends are the same shape — so the table can be seeded from a fetch and re-seeded from
 * what was just written without a second translation.
 *
 * `status` rides along on each item so re-saving the table preserves a milestone that has
 * already been achieved or invoiced rather than resetting it.
 */
function withMilestones(
  values: Record<string, any>,
  rows: Array<{
    milestone_number: number;
    payment_percent: number | string | null;
    milestone: string | null;
    milestone_status: string | null;
  }>
): Record<string, any> {
  return {
    ...values,
    milestones: {
      ...values.milestones,
      count: rows.length,
      items: rows.map((row, index) => ({
        index: row.milestone_number ?? index + 1,
        percentage: row.payment_percent == null ? '' : Number(row.payment_percent),
        label: row.milestone ?? '',
        status: row.milestone_status ?? 'not_started',
      })),
    },
  };
}
